/**
 * Environment Configuration Utilities
 * 
 * Helper functions to determine current environment and access env variables
 */

// Environment detection
export const isDevelopment = import.meta.env.MODE === 'development'
export const isStaging = import.meta.env.MODE === 'staging'
export const isProduction = import.meta.env.MODE === 'production'

// Environment name
export const environment = import.meta.env.VITE_ENV || import.meta.env.MODE || 'development'

// App configuration
export const appName = import.meta.env.VITE_APP_NAME || 'Wojska Specjalne Platform'

// App version (from package.json, injected via Vite define)
export const appVersion = import.meta.env.VITE_APP_VERSION || '0.0.0'

// Build timestamp (injected during build)
export const buildTime = import.meta.env.VITE_BUILD_TIME || 'unknown'

// Debug mode (only in development)
export const isDebugMode = import.meta.env.VITE_DEBUG === 'true' && isDevelopment

// Analytics
export const gaId = import.meta.env.VITE_GA_ID

/**
 * Get environment display name with emoji
 */
export const getEnvironmentBadge = (): string => {
  if (isDevelopment) return '🔧 Development'
  if (isStaging) return '🧪 Staging'
  if (isProduction) return '🚀 Production'
  return '❓ Unknown'
}

/**
 * Log environment info (only in non-production)
 */
export const logEnvironmentInfo = (): void => {
  if (isProduction) return

  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
  console.log(`🌍 Environment: ${getEnvironmentBadge()}`)
  console.log(`📱 App Name: ${appName}`)
  console.log(`📦 Version: ${appVersion}`)
  console.log(`🕐 Build Time: ${buildTime}`)
  console.log(`🔧 Mode: ${import.meta.env.MODE}`)
  console.log(`🐛 Debug: ${isDebugMode ? 'Enabled' : 'Disabled'}`)
  console.log(`🔗 Supabase: ${import.meta.env.VITE_SUPABASE_URL || 'Not configured'}`)
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
}

/**
 * Check if feature flag is enabled
 */
export const isFeatureEnabled = (featureName: string): boolean => {
  const envVar = `VITE_FEATURE_${featureName.toUpperCase()}`
  return import.meta.env[envVar] === 'true'
}

/**
 * Get API URL based on environment
 */
export const getApiUrl = (): string => {
  // Możesz dodać różne API URLs dla różnych środowisk
  return import.meta.env.VITE_API_URL || '/api'
}

// Export for convenience
export default {
  isDevelopment,
  isStaging,
  isProduction,
  environment,
  appName,
  appVersion,
  buildTime,
  isDebugMode,
  gaId,
  getEnvironmentBadge,
  logEnvironmentInfo,
  isFeatureEnabled,
  getApiUrl,
}

